import React, { type FC, useRef } from 'react';
import styled from '@emotion/styled';
import type { ImageDataLike } from 'gatsby-plugin-image';
import ReactPlayer from 'react-player';
import { css } from '@emotion/react';
import { Image } from '../Image/Image';
import { VideoPlayIcon } from '../icon';

// Interface

export interface VideoProps {
  url?: string;
  image?: ImageDataLike | string;
  autoPlay?: boolean;
}

// Styles

const StyledVideoContainer = styled.div`
  ${({ theme: { space, borderRadii } }) => css`
    position: relative;
    padding-top: 56.25%; /* Player ratio: 100 / (1280 / 720) */
    margin-bottom: ${space.xLarge};
    border-radius: ${borderRadii.medium};
    overflow: hidden;

    .react-player {
      position: absolute;
      top: 0;
      left: 0;
    }

    .react-player__preview {
      &:before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.3);
      }

      img {
        width: 100%;
        height: 100%;
      }

      svg {
        position: absolute;
      }
    }
  `};
`;

export const Video: FC<VideoProps> = ({ url, image, autoPlay }) => {
  const ref = useRef<ReactPlayer & HTMLDivElement>(null);

  const handleError = (e: any) => {
    console.error('Video playback error:', e);
  };

  const lightProps = autoPlay
    ? { light: false }
    : {
        light: image ? (
          <Image
            src={image}
            alt="Thumbnail"
            loading="eager"
            roundCorners="all"
            role="button"
          />
        ) : (
          true
        ),
      };
  const containerRef = useRef<HTMLDivElement>(null);
  return (
    <StyledVideoContainer ref={containerRef} tabIndex={-1}>
      <ReactPlayer
        id="react-player"
        tabIndex={0}
        {...lightProps}
        className="react-player"
        url={url}
        ref={ref}
        playIcon={
          <VideoPlayIcon
            size="xLarge"
            tabIndex={0}
            aria-label="Play Video"
            role="button"
          />
        }
        playing={autoPlay}
        width="100%"
        height="100%"
        onError={handleError}
        controls
        config={{
          youtube: {
            playerVars: { rel: 0, modestBranding: 1 },
          },
        }}
        previewTabIndex={0}
        onClickPreview={() => {
          const elementToFocus = containerRef?.current;
          elementToFocus?.focus?.();
        }}
      />
    </StyledVideoContainer>
  );
};

export default Video;
